// What went wrong when a lab order template was submitted, in terms the page can
// act on. Every kind names the next action for the GP — a validation failure is
// fixed in the patient's record, a stale facility list is reselected, a
// Healthlink refusal is retried or reported.
export const LAB_ORDER_ERROR_KIND = {
  validation: "validation",
  staleFacilities: "staleFacilities",
  healthlinkRefused: "healthlinkRefused",
  // Refused by our own backend for a reason that is not the patient's data — a bad
  // id, a destination it re-checked. Its own kind, so it never tells the GP to go and
  // edit a record that is fine.
  refused: "refused",
  // Healthlink itself cannot be used at all — not configured, practice or certificate not
  // set up, or login refused (KIN-2094). Its own kind because no amount of retrying or
  // reselecting fixes it: somebody has to set Healthlink up.
  healthlinkUnavailable: "healthlinkUnavailable",
  // The order cannot be placed under the GP that was chosen (KIN-2100). Its own kind
  // because the thing to change is the ordering GP, and nothing else on the form.
  orderingGpRefused: "orderingGpRefused",
  auth: "auth",
  unknown: "unknown",
};

// The backend names HL7 fields in its violation messages (it validates the
// message it is about to send). A GP has no idea what PID.8 is, so each known
// segment/field is renamed to the thing they would go and edit.
const HL7_FIELD_LABELS = {
  "PID.3": "Patient identifier",
  "PID.5": "Patient name",
  "PID.7": "Date of birth",
  "PID.8": "Patient gender",
  "PID.11": "Patient address",
  "MSH.4": "Practice Healthlink details",
  "MSH.6": "Destination laboratory",
  "MSH.7": "Message date",
  "MSH.10": "Message reference",
  "PV1.8": "Referring doctor",
};

const HL7_FIELD_PATTERN = /^([A-Z]{2,3}\.\d{1,2})\s*:\s*/;

// "PID.8: the patient's gender must be recorded as…" →
// "Patient gender: the patient's gender must be recorded as…"
export const toGpReadableViolation = (message) => {
  if (!message) return "";

  const match = message.match(HL7_FIELD_PATTERN);

  if (!match) return message;

  const label = HL7_FIELD_LABELS[match[1]];

  if (!label) return message;

  return `${label}: ${message.slice(match[0].length)}`;
};

// Axios's own text for a failed request ("Request failed with status code 400").
// It is not a message: it names the transport, tells a GP nothing, and is what
// leaks through whenever the API answered without a message of its own.
const AXIOS_FALLBACK_MESSAGE = /^request failed with status code \d+$/i;

const readableMessage = (message) => {
  if (!message || AXIOS_FALLBACK_MESSAGE.test(String(message).trim())) return "";

  return message;
};

const readViolations = (error) => {
  const errors = error?.response?.data?.errors;

  if (!Array.isArray(errors)) return [];

  return errors
    .map((item) => toGpReadableViolation(item?.message))
    .filter(Boolean);
};

// A refused destination is not always a 409. Verified against UAT: submitting a
// hospital that is not lab-capable answers **400** with Spring's default error body,
// not the documented 409. So the destination case is recognised by what the backend
// said, not only by the status it chose.
const UNORDERABLE_HOSPITAL = /does not accept laboratory orders/i;

/*
 * The other things a 409 means here (KIN-2100). They used to be the same 409 as a stale
 * destination, so a practice that simply has no lab ordering — or a nurse placing an
 * order under a GP, which Healthlink hasn't confirmed yet — was told the laboratory
 * list was out of date and asked to select it again. Which it could do all day.
 */
const ORDERING_GP_REFUSED = /under another gp|orderingstaffid/i;
const LAB_ORDERING_UNAVAILABLE =
  /laboratory ordering is not enabled|no healthlink credentials/i;

// ErrorCode.HEALTHLINK_ERROR, which ApiExceptionHandler and the CDM handler both answer a
// HealthlinkUnavailableException with (KIN-2094).
const HEALTHLINK_ERROR_CODE = "HEALTHLINK_ERROR";

// The backend answers in two shapes: the ApiResponse envelope (whose `message`
// axiosConfig lifts onto error.message) and Spring's default error body, which it
// does not lift because that body has no `status: "error"`. Reading the response
// directly covers both, and keeps the backend's own wording instead of axios's.
const readApiMessage = (error) =>
  readableMessage(error?.response?.data?.message) ||
  readableMessage(error?.message);

export const mapLabOrderError = (error) => {
  const status = error?.response?.status;
  const message = readApiMessage(error);
  const code = error?.response?.data?.code;

  /*
   * Checked before the 409, and before anything else: "Healthlink can't be used" moved from
   * 409 to 503 (KIN-2094), and while it was a 409 it landed here as a stale facility list —
   * so a practice with no Healthlink set-up was told to reselect the laboratory, which it
   * could do all day without getting anywhere. The backend's own sentence is short and says
   * what to do, so it is shown as-is.
   *
   * Recognised by the error code as well as the status, the way the facility case below
   * already reads CONFLICT: HEALTHLINK_ERROR is what the handler actually names this, and a
   * code is a more specific statement than a status any number of things can carry.
   */
  if (status === 503 || code === HEALTHLINK_ERROR_CODE) {
    return { kind: LAB_ORDER_ERROR_KIND.healthlinkUnavailable, message };
  }

  if (ORDERING_GP_REFUSED.test(message)) {
    return { kind: LAB_ORDER_ERROR_KIND.orderingGpRefused, message };
  }

  if (LAB_ORDERING_UNAVAILABLE.test(message)) {
    return { kind: LAB_ORDER_ERROR_KIND.healthlinkUnavailable, message };
  }

  if (UNORDERABLE_HOSPITAL.test(message) || code === "CONFLICT" || status === 409) {
    return { kind: LAB_ORDER_ERROR_KIND.staleFacilities, message };
  }

  if (status === 400) {
    const items = readViolations(error);

    // A 400 with a violation list is about the patient's data. A 400 without one is
    // something else the backend refused (a bad id, a destination it re-checked), so
    // it must not be reported as "fix the patient's details".
    return items.length > 0
      ? { kind: LAB_ORDER_ERROR_KIND.validation, items }
      : { kind: LAB_ORDER_ERROR_KIND.refused, message };
  }

  if (status === 422) {
    return { kind: LAB_ORDER_ERROR_KIND.healthlinkRefused, message };
  }

  if (status === 401 || status === 403) {
    return { kind: LAB_ORDER_ERROR_KIND.auth, message };
  }

  return { kind: LAB_ORDER_ERROR_KIND.unknown, message };
};

// Headline shown above the error panel. The validation list carries its own
// per-item text, so its headline stays generic.
export const getLabOrderErrorTitle = (mappedError) => {
  switch (mappedError?.kind) {
    case LAB_ORDER_ERROR_KIND.validation:
      return "This lab order can't be sent yet";
    case LAB_ORDER_ERROR_KIND.staleFacilities:
      return "The laboratory list is out of date";
    case LAB_ORDER_ERROR_KIND.healthlinkRefused:
      return "Healthlink refused this lab order";
    case LAB_ORDER_ERROR_KIND.refused:
      return "This lab order couldn't be sent";
    case LAB_ORDER_ERROR_KIND.healthlinkUnavailable:
      return "Healthlink isn't available";
    case LAB_ORDER_ERROR_KIND.orderingGpRefused:
      return "This order can't be placed under that GP";
    case LAB_ORDER_ERROR_KIND.auth:
      return "Your session has expired";
    default:
      return "Something went wrong sending this lab order";
  }
};

// One line for a toast: what happened and what to do, in that order. Used instead
// of an in-page panel, so it must stand on its own and must never carry a raw
// transport message.
export const getLabOrderErrorToast = (mappedError) => {
  const title = getLabOrderErrorTitle(mappedError);
  const hint = getLabOrderErrorHint(mappedError);

  if (mappedError?.kind === LAB_ORDER_ERROR_KIND.validation) {
    const items = (mappedError.items ?? []).filter(Boolean);

    return items.length > 0 ? `${title}: ${items.join(" ")}` : `${title}. ${hint}`;
  }

  const detail = readableMessage(mappedError?.message);

  return detail ? `${title}: ${detail}` : `${title}. ${hint}`;
};

// The action to offer alongside the headline.
export const getLabOrderErrorHint = (mappedError) => {
  switch (mappedError?.kind) {
    case LAB_ORDER_ERROR_KIND.validation:
      return "Update the patient's details, then try again.";
    case LAB_ORDER_ERROR_KIND.staleFacilities:
      return "We've refreshed the list — please select the laboratory again.";
    case LAB_ORDER_ERROR_KIND.healthlinkRefused:
      return "You can try again. If it keeps failing, report the message above.";
    case LAB_ORDER_ERROR_KIND.refused:
      return "Check the destination laboratory and try again.";
    case LAB_ORDER_ERROR_KIND.healthlinkUnavailable:
      // No "try again": nothing the GP does at this screen changes it.
      return "Contact Kinesin support.";
    case LAB_ORDER_ERROR_KIND.orderingGpRefused:
      return "Choose a different ordering GP, or sign in as that GP to place it.";
    case LAB_ORDER_ERROR_KIND.auth:
      return "Sign in again to place this order.";
    default:
      return "Please try again.";
  }
};
